import RiveComponent, { RiveFit } from '../../../components/RiveComponent'
import { useAppMetaContent } from '../../../state-management/providers/AppMetaContentProvider'

type TrustStatementVariant = 'elite' | 'elitePrime'

const ASSETS = {
  eliteWordmark: '/images/new-elite/footer/logo.png',
  primeWordmark: '/images/elite-plans/elite-prime/footer/logo.png',
  powerupMoneyLogo: '/images/new-elite/footer/powerup-money-logo.svg',
  backgroundVector: '/images/new-elite/footer/background-vector.svg'
} as const

const PRIME_BACKGROUND = 'linear-gradient(180deg, #FFE9ED 23.73%, #FFEFF2 52.92%)'

const VARIANT: Record<
  TrustStatementVariant,
  {
    wordmark: string
    wordmarkAlt: string
    className: string
    topFadeClassName: string
    accentTextClassName: string
    style?: { background: string }
  }
> = {
  elite: {
    wordmark: ASSETS.eliteWordmark,
    wordmarkAlt: 'Elite',
    className: 'bg-gradient-to-b from-[#FBF4F1] from-[23.728%] to-white to-[52.919%]',
    topFadeClassName: 'to-elite10',
    accentTextClassName: 'text-elite50'
  },
  elitePrime: {
    wordmark: ASSETS.primeWordmark,
    wordmarkAlt: 'Elite Prime',
    className: '',
    topFadeClassName: 'to-prime10',
    accentTextClassName: 'text-prime50',
    style: { background: PRIME_BACKGROUND }
  }
}

export default function NewEliteLandingTrustStatement({
  variant = 'elite',
  wordmarkRiveSrc,
  hideBackground = false
}: {
  variant?: TrustStatementVariant
  /** Replaces the static wordmark with a Rive animation; static image is the load-failure fallback. */
  wordmarkRiveSrc?: string
  /** Drops the variant gradient and the decorative background vector. */
  hideBackground?: boolean
}) {
  const look = VARIANT[variant]
  const { getInvestorsDisplay, getAumTrackedDisplay, getEliteMembersDisplay } = useAppMetaContent()

  return (
    <div>
      <div aria-hidden='true' className={`h-10 bg-gradient-to-t from-[#FEFFFF] ${look.topFadeClassName}`} />
      <div
        className={`relative overflow-hidden px-5 pb-[70px] pt-2 z-0 ${hideBackground ? '' : look.className}`}
        style={hideBackground ? undefined : look.style}
      >
        {hideBackground ? null : (
          <img
            alt=''
            aria-hidden
            className='pointer-events-none absolute left-1/2 top-[-150px] z-0 size-[929px] -translate-x-1/2'
            src={ASSETS.backgroundVector}
          />
        )}

        <div className='relative z-[1] flex flex-col items-center'>
          {wordmarkRiveSrc ? (
            <div role='img' aria-label={look.wordmarkAlt} className='aspect-[300/273] w-full max-w-[300px]'>
              <RiveComponent
                src={wordmarkRiveSrc}
                stateMachines='State Machine 1'
                fit={RiveFit.FILL}
                playOnView
                fallback={
                  <img alt='' className='h-full w-full object-contain' src={look.wordmark} />
                }
              />
            </div>
          ) : (
            <img
              alt={look.wordmarkAlt}
              className='h-[52px] w-[169px] object-contain'
              src={look.wordmark}
            />
          )}
          {/* --ep-footer-lead is only set on elite plans pages; other pages keep grey10 */}
          <p className='w-[275px] text-center text-[20px] leading-[1.3] text-[color:var(--ep-footer-lead,theme(colors.grey10))] mt-2'>
            You work hard for your money.{' '}
            <span className={`font-medium ${look.accentTextClassName}`}>
              We work just as hard to look after it.
            </span>
          </p>
        </div>

        <div className='relative z-[1] mt-20 flex flex-col items-center gap-5'>
          <div className='flex h-5 items-center justify-center'>
            <img
              alt='PowerUp Money'
              className='h-[16px] w-[130px] object-contain'
              src={ASSETS.powerupMoneyLogo}
            />
          </div>
          <div className='w-[327px] text-center text-xs leading-[1.4] text-[#6A6E6E]'>
            <p>
              We are India's trusted mutual fund advisory app. In just a year, we now have{' '}
              {getInvestorsDisplay('long') ?? ''} investors, {getAumTrackedDisplay('long') ?? ''} AUM
              tracked, and {getEliteMembersDisplay('long') ?? ''} Elite members.
            </p>
            <p className='mt-3'>
              PowerUp is the fastest-growing mutual fund advisory platform in India. We are
              backed by Accel, Blume, Kae Capital, and some of the world&apos;s best investors.
            </p>
            <p className='mt-3'>
              We built Elite so you can get the same edge the top 1% rely on, and we can&apos;t
              wait for you to experience it.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
